import React, { useRef, useState } from 'react'
import styled from 'styled-components'

import Icon from '../../Icon'
import SearchIcon from '../../../assets/search.svg'
import Close from '../../../assets/close.svg'
import breakpoints from '../../../constants/breakpoints'

interface Props {
  buttonText: string
  label?: string
  placeholder: string
  handleOnChange?: (value: string) => void
  handleOnKeyDown?: (event: React.KeyboardEvent) => void
  handleOnSubmit?: (value: string) => void
  handleToggleFocus?: (value: boolean) => void
  handleOnBlur?: () => void
}

const Search: React.FC<Props> = ({
  buttonText,
  placeholder,
  label,
  handleOnChange,
  handleOnKeyDown,
  handleOnSubmit,
  handleToggleFocus,
}) => {
  const [value, setValue] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchFieldInFocus, setSearchFieldInFocus] = useState(false)

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)

    if (handleOnChange) return handleOnChange(event.target.value)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (handleOnSubmit) return handleOnSubmit(value)
  }

  const handleClearField = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setValue('')
    inputRef.current?.focus()
    if (handleOnChange) return handleOnChange('')
  }

  const valueIsSet = Boolean(value?.length)
  const showActionButtons = searchFieldInFocus || valueIsSet

  return (
    <>
      <Form id="search-form" onSubmit={(event) => handleSubmit(event)}>
        <InputWrapper>
          <SearchIconWrapper>
            <Icon icon={SearchIcon} />
          </SearchIconWrapper>
          {label && <Label htmlFor="search">{label}</Label>}
          <Input
            id="search"
            autoComplete="off"
            placeholder={placeholder}
            ref={inputRef}
            value={value}
            onChange={(event) => onChange(event)}
            onKeyDown={handleOnKeyDown}
            onClick={() => {
              // Also trigger if user pressed escape, but input is still in focus
              handleToggleFocus?.(true)
            }}
            onFocus={() => {
              handleToggleFocus?.(true)
              setSearchFieldInFocus(true)
            }}
            onBlur={() => {
              if (!value?.length) {
                handleToggleFocus?.(false)
              }
              setSearchFieldInFocus(false)
            }}
          />
          <ClearTextButton
            onClick={handleClearField}
            $isVisible={showActionButtons}
            type="button"
            aria-hidden={!showActionButtons}
            aria-focusable={showActionButtons}
            disabled={!showActionButtons}
          >
            <span className="sr-only">Het zoekveld leegmaken</span>
            <Icon icon={Close} />
          </ClearTextButton>
        </InputWrapper>
        <SubmitButton
          form="search-form"
          $isVisible={showActionButtons}
          type="submit"
          disabled={!showActionButtons}
          aria-hidden={!showActionButtons}
          aria-focusable={showActionButtons}
        >
          {buttonText}
        </SubmitButton>
      </Form>
    </>
  )
}

const Form = styled.form`
  /* stylelint-disable, no-descending-specificity */

  position: relative;
  display: flex;
  align-items: center;
  margin-left: 32px;
  width: 100%;
  max-width: 600px;

  svg {
    width: 18px;
    height: 18px;

    g {
      stroke: ${({ theme }) => theme.green};
      transition: stroke 0.3s ease;
    }
  }

  &:focus-within {
    svg {
      g {
        stroke: ${({ theme }) => theme.blue};
      }
    }
  }

  @media (max-width: ${breakpoints.lg}) {
    margin-left: 16px;
  }
`

const InputWrapper = styled.div`
  position: relative;
  flex: 1 1 100%;
`

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 6px;
  width: 50px;
  height: 50px;
`

const ClearTextButton = styled.button<{ $isVisible: boolean | undefined }>`
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  cursor: pointer;
  background: none;
  border: 0 none;
  transition:
    visibility 0.3s ease,
    opacity 0.3s ease;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  outline-offset: initial !important;

  & svg {
    width: 8px;
    height: 8px;
    margin-bottom: 2px;
    fill: ${({ theme }) => theme.blue};
  }

  &:active {
    outline: none;
  }

  ${({ $isVisible }) =>
    !$isVisible &&
    `
    visibility: hidden;
    opacity: 0;
    cursor: default;
  `}
`

const Label = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`

const Input = styled.input`
  color: ${({ theme }) => theme.textColor};
  font-family: 'Circular', sans-serif;
  font-size: 20px;
  border: none;
  width: 100%;
  height: 50px;
  transition: 0.3s border-color;

  &::placeholder {
    color: ${({ theme }) => theme.warmGrey};
    opacity: 1;

    @media (max-width: ${breakpoints.md}) {
      font-size: 18px;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 4px 13px 0 rgba(0, 0, 0, 0.12);
    border-color: ${({ theme }) => theme.blue};
  }
`

const SubmitButton = styled.button<{ $isVisible: boolean | undefined }>`
  cursor: pointer;
  display: flex;
  padding-left: 20px;
  background: transparent;
  border: none;
  opacity: 1;
  height: 33px;
  line-height: 33px;
  color: ${({ theme }) => theme.blue};
  font-size: 20px;
  transition:
    visibility 0.3s,
    opacity 0.3s;

  @media (max-width: ${breakpoints.sm}) {
    display: none;
    cursor: default;
  }

  @media (max-width: ${breakpoints.md}) {
    font-size: 18px;
  }

  :focus {
    outline-color: ${({ theme }) => theme.yellow};
  }

  ${({ $isVisible }) =>
    !$isVisible &&
    `
    visibility: hidden;
    opacity: 0;
    cursor: default;
  `}
`

export default Search
